import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../reduxStore/redux-hooks';
import LoadDynamicMessageOnChat from './LoadDynamicMessageOnChat';
import { LlmModel } from '../../data/models/LlmModel';
import SouthIcon from '@mui/icons-material/South';
import UseScrollHandler from '../../components/sharedComponents/customHooks/useScrollHandler/UseScrollHandler';

interface ResponseBoxProps {
  userFirstName: string;
  dynamicContent: ReactNode;
  modelIcon: string;
  modelTooltip?: string;
}

const ResponseBox: React.FC<ResponseBoxProps> = ({ userFirstName, dynamicContent, modelIcon, modelTooltip }) => {
  const { t } = useTranslation();
  const responseBoxRef = useRef<HTMLDivElement>(null);
  const { fileUpload } = useAppSelector((state) => state.fileUpload);
  const clearValue = useAppSelector((state) => state.history.historyState.clearValue);
  const [modelIconToShow, setModelIconToShow] = useState(LlmModel.defaultModal.image);
  const { showScrollButton, handleScrollToBottom } = UseScrollHandler(responseBoxRef, dynamicContent);

  useEffect(() => {
    if (modelIcon && (clearValue || modelIconToShow !== modelIcon)) {
      setModelIconToShow(modelIcon);
    }
  }, [clearValue, modelIcon, modelIconToShow]);

  return (
    <Box
      component="section"
      id="response-box"
      ref={responseBoxRef}
      sx={{
        position: 'relative',
        maxHeight: { xs: 'calc(-19rem + 100vh)', sm: 'calc(-14rem + 100vh)' },
        height: `calc(100vh - ${fileUpload.uploadFile ? '16.9rem' : '13.9rem'})`,
      }}
    >
      <Box className="welcomeMsgOuter highlight-background-content">
        <Box className="welcome-icon">
          <img id="changeicon" src={modelIconToShow} alt="ChatGPT icon" />
          {modelTooltip && (
            <Box className="toolTip">
              <strong>{modelTooltip}</strong>
            </Box>
          )}
        </Box>
        <Box className="welcome-msg">
          <Typography variant="h2" className="userName">
            {t('chatInterface.welcomeMessage', { name: userFirstName })}
          </Typography>
          <Typography>{t('chatInterface.greeting')}</Typography>
        </Box>
      </Box>
      {dynamicContent}
      {showScrollButton && (
        <Box
          onClick={handleScrollToBottom}
          sx={{
            position: 'fixed',
            bottom: '10rem',
            right: '50%',
            cursor: 'pointer',
            backgroundColor: '#fff',
            color: 'black',
            padding: '5px',
            borderRadius: '50%',
            border: '1px solid #ccc',
            width: '25px',
            height: '25px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <SouthIcon />
        </Box>
      )}
      <Box>{LoadDynamicMessageOnChat(responseBoxRef)}</Box>
    </Box>
  );
};

export default ResponseBox;
